import { action, computed, makeObservable, observable } from 'mobx';
import * as R from 'ramda';
import { AnswerByIdT, AnswerT } from 'src/api/types/AnswerT';
import { ExplanationT } from 'src/api/types/ExplanationT';
import { QuestionT } from 'src/api/types/QuestionT';
import { ScoreRangeT } from 'src/api/types/ScoreRangeT';
import { filterAnswers } from 'src/selfTest/SelfTestState/filterAnswers';
import { trackEvent } from '../../tracking/utils/initTracking';

export class AnswersState {
  @observable answerByQuestionId: AnswerByIdT = {};
  @observable scoreRangeSet: ScoreRangeT[];

  constructor(scoreRangeSet: ScoreRangeT[]) {
    this.scoreRangeSet = scoreRangeSet;
    makeObservable(this);
  }

  getScoreRange = (score: number): ScoreRangeT | undefined => {
    for (const scoreRange of this.scoreRangeSet) {
      if (score <= scoreRange.upperScore) {
        return scoreRange;
      }
    }
    return R.last(this.scoreRangeSet);
  };

  @action
  answerQuestion = (question: QuestionT, answer: AnswerT, questionDisplayNr: number) => {
    trackEvent('click_answer', 'Question Details', `${questionDisplayNr}_${answer.text}`);

    this.answerByQuestionId[question.id] = answer;
  };

  @action
  deleteAnswer = (question: QuestionT) => {
    delete this.answerByQuestionId[question.id];
  };

  @computed
  get givenAnswers() {
    return R.values(this.answerByQuestionId);
  }

  @computed
  get riskfactors(): ExplanationT[] {
    return filterAnswers('RISKFACTOR', this.givenAnswers);
  }

  @computed
  get tips(): ExplanationT[] {
    return filterAnswers('TIP', this.givenAnswers);
  }

  @computed
  get totalScore() {
    return R.pipe(R.always(this.givenAnswers), R.map(R.prop('score')), R.sum)();
  }

  getExplanation(explanationPageIdx: number) {
    return explanationPageIdx < this.riskfactors.length
      ? this.riskfactors[explanationPageIdx]
      : this.tips[explanationPageIdx - this.riskfactors.length];
  }
}
